/* Expense > Reports — wired to the accounting-api (Phase 1).
   KPI cards row (derived from the live list), status filter pills, and the reports
   table (status badges + cyan link actions). Data comes from GET /v1/expense/reports
   via TanStack Query (src/expense/hooks/use-reports.ts); "New report" creates the
   current month's report idempotently. */
import { PlusIcon } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  Button,
  Card,
  cn,
  Kpi,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@andavo/web-ui';

import {
  useCreateReport,
  useReports,
  type ExpenseReport,
  type ReportStatus,
} from './hooks/use-reports';
import { StatusBadge } from './statusBadge';

type StatusFilter = 'all' | ReportStatus;

const FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'OPEN', label: 'Open' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

function reference(r: ExpenseReport): string {
  return `EXP-${r.periodCode}-${String(r.reportId).padStart(4, '0')}`;
}

function shortOid(oid: string): string {
  return oid.slice(0, 8);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function ReportsScreen() {
  const [filter, setFilter] = useState<StatusFilter>('all');
  const {
    data: reports = [],
    isLoading,
    isError,
  } = useReports(filter === 'all' ? undefined : filter);
  const createReport = useCreateReport();
  const navigate = useNavigate();

  const kpis = useMemo(() => {
    const by = (s: ReportStatus) =>
      reports.filter((r) => r.status === s).length;
    return [
      {
        key: 'open',
        label: 'Open',
        value: String(by('OPEN')),
        hint: 'Not yet submitted',
      },
      {
        key: 'submitted',
        label: 'Awaiting approval',
        value: String(by('SUBMITTED')),
        hint: 'In review',
      },
      {
        key: 'approved',
        label: 'Approved',
        value: String(by('APPROVED')),
        hint: 'Posted to the ledger',
      },
      {
        key: 'rejected',
        label: 'Rejected',
        value: String(by('REJECTED')),
        hint: 'Needs changes',
      },
    ];
  }, [reports]);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((kpi) => (
          <Kpi
            key={kpi.key}
            label={kpi.label}
            value={kpi.value}
            hint={kpi.hint}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => {
            const active = filter === f.value;
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                aria-pressed={active}
                className={cn(
                  'rounded-full border px-3 py-1.5 font-inter text-sm transition-colors',
                  active
                    ? 'border-border-action bg-surface-action-subtle text-content-action'
                    : 'border-border-subtle bg-surface-midground text-content-secondary hover:border-border-medium hover:text-content-primary',
                )}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <Button
          variant="primary"
          size="sm"
          disabled={createReport.isPending}
          onClick={() =>
            // Get-or-create is idempotent (V018: one OPEN report per period), so this may return
            // an EXISTING open report — navigate to it either way so the click always lands the user
            // on the report instead of silently doing nothing when one already exists.
            createReport.mutate(
              { period: currentPeriod() },
              {
                onSuccess: (report) =>
                  navigate(`/expense/reports/${report.reportId}`),
              },
            )
          }
        >
          <PlusIcon className="me-2 size-icon-sm" />
          New report
        </Button>
      </div>

      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Reference</TableHead>
              <TableHead>Report</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map((r) => (
              <TableRow key={r.reportId} data-report-row>
                <TableCell className="font-inter font-semibold text-content-primary">
                  {reference(r)}
                </TableCell>
                <TableCell className="text-content-primary">
                  {r.title ?? r.periodCode}
                </TableCell>
                <TableCell className="text-content-secondary">
                  {shortOid(r.ownerUserOid)}
                </TableCell>
                <TableCell className="text-content-secondary">
                  {r.periodCode}
                </TableCell>
                <TableCell className="text-content-secondary">
                  {formatDate(r.submittedAt)}
                </TableCell>
                <TableCell>
                  <StatusBadge status={r.status} />
                </TableCell>
                <TableCell className="text-right">
                  <Link
                    to={`/expense/reports/${r.reportId}`}
                    className="font-inter text-content-action text-sm hover:underline"
                  >
                    View
                  </Link>
                </TableCell>
              </TableRow>
            ))}
            {isLoading && (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={7}
                  className="py-10 text-center text-content-secondary"
                >
                  Loading reports…
                </TableCell>
              </TableRow>
            )}
            {isError && !isLoading && (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={7}
                  className="py-10 text-center text-content-danger"
                >
                  Couldn’t load reports. Try again.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && reports.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={7}
                  className="py-10 text-center text-content-secondary"
                >
                  No reports match this filter.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
